import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  LinearProgress,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { US_STATES } from 'src/constants/usStates';
import { DimmableArea } from './DimmableArea.styles';
import { StripedList } from './StripedList.styles';
import { SignupNumberFilter } from '../../controllers/SignupPhoneNumberViewController';
import { formatSignupNumber } from '../../helpers/phoneNumber';
import { fillColumnSx, noticeSx } from '../../helpers/sx';
import { SignupAreaCode, SignupAvailableNumber } from '../../types';

type Props = {
  numbers: SignupAvailableNumber[];
  totalResults: number | null;
  searching: boolean;
  appliedFilter: SignupNumberFilter;
  /** What the controls start on, which is the state from the company address rather than the whole country. */
  openingFilter: SignupNumberFilter;
  /** The city from that address, shown as the example — a place the client has nothing to do with reads as
   * a suggestion of somewhere to look. */
  companyCity: string | null;
  areaCodes: SignupAreaCode[];
  loadingAreaCodes: boolean;
  choosing: string | null;
  error: string | null;
  chosenNumber: string | null;
  chosenHeld: boolean;
  onSearch: (filter: SignupNumberFilter) => void;
  onLoadAreaCodes: (state: string) => void;
  onChoose: (phoneNumber: string) => void;
  onContinueWithChosen: () => void;
};

// Long enough that a search does not fire on every keystroke, short enough to feel like typing into a list.
const TYPING_DEBOUNCE_MS = 400;

const ALL_STATES = 'All states';

/**
 * The inventory moves constantly and the exact figure is Telnyx's, not ours — a stale "27669" reads as a
 * promise. An open range says the same thing without pretending to a precision the next request may not
 * honour, and it is short enough to sit inside a dropdown row.
 */
const RANGE_STEPS = [10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000];

const countLabel = (count: number | null): string | null => {
  if (count == null || count <= 0) return null;

  const step = [...RANGE_STEPS].reverse().find(threshold => count > threshold);
  return step == null ? `${count}` : `>${step}`;
};

const placeOf = (number: SignupAvailableNumber): string => {
  return [number.locality, number.administrativeArea].filter(part => part != null).join(', ');
};

/**
 * Where the search looked, in words. Narrowest first: an area code says more than the state holding it, and a
 * city more than the state it is in.
 */
const placeDescription = (filter: SignupNumberFilter): string | null => {
  if (filter.areaCode.length > 0) return `area code ${filter.areaCode}`;
  if (filter.city.length > 0) {
    return filter.state.length > 0 ? `${filter.city}, ${filter.state}` : filter.city;
  }
  return filter.state.length > 0
    ? US_STATES.find(state => state.code === filter.state)?.name ?? filter.state
    : null;
};

/**
 * Why an empty answer is empty. Telnyx reports a pattern nothing matched the same way it reports an empty
 * catalogue, so a search that worked must not be answered with "try again in a moment" — and the pattern and
 * the place are named separately, because a sentence built by gluing them together reads as neither.
 */
const emptyResultText = (filter: SignupNumberFilter): string => {
  const place = placeDescription(filter);
  const digits = filter.contains;

  /*
    A city with nothing left, a city typed half-way and a city misspelt all come back empty, and the answer
    does not say which: the carrier matches a locality exactly, with no search by the beginning of a name.
    So where a city was asked for, both readings are offered and neither is claimed — naming one of them
    tells whoever typed the name correctly something untrue about it.
  */
  if (filter.city.length > 0) {
    const containing = digits.length === 0 ? '' : ` containing ${digits}`;
    return `No numbers found for ${place}${containing}. Either none are free there, or the city name is `
      + 'not an exact match. Try somewhere else, or clear the filters.';
  }

  if (digits.length === 0) {
    return place == null
      ? 'No numbers are available right now. Please try again in a moment.'
      : `No numbers are free in ${place} right now. Try somewhere else, or clear the filters.`;
  }

  return place == null
    ? `No available number contains ${digits}. Try other digits, or clear the filters.`
    : `No available number in ${place} contains ${digits}. Try something else, or clear the filters.`;
};

const sliceText = (filter: SignupNumberFilter, shown: number, total: number): string => {
  const place = placeDescription(filter);
  const scope = [
    place == null ? null : `in ${place}`,
    filter.contains.length === 0 ? null : `containing ${filter.contains}`,
  ].filter(part => part != null).join(' ');

  return `Showing ${shown} of ${total} available${scope.length > 0 ? ` ${scope}` : ''}.`;
};

const Heading: React.FC = () => (
  <>
    <Typography variant="h4" fontWeight="fontWeightSemiBold" gutterBottom>
      Pick your phone number
    </Typography>
    <Typography variant="body1" color="text.secondary" mb={4}>
      This is the number your messages will come from. We hold it for you while you finish signing up.
    </Typography>
  </>
);

export const SignupPhoneNumberView: React.FC<Props> = ({
  numbers,
  totalResults,
  searching,
  appliedFilter,
  openingFilter,
  companyCity,
  areaCodes,
  loadingAreaCodes,
  choosing,
  error,
  chosenNumber,
  chosenHeld,
  onSearch,
  onLoadAreaCodes,
  onChoose,
  onContinueWithChosen,
}) => {
  const [filter, setFilter] = useState<SignupNumberFilter>(openingFilter);

  /**
   * One timer for every control. The two typed fields wait for the typing to stop; the state and a code
   * picked from the list search at once, and cancel a wait already running rather than being overtaken by it.
   */
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (timer.current != null) clearTimeout(timer.current);
  }, []);

  const apply = (next: SignupNumberFilter, when: 'now' | 'afterTyping') => {
    setFilter(next);
    if (timer.current != null) clearTimeout(timer.current);

    if (when === 'now') {
      onSearch(next);
      return;
    }
    timer.current = setTimeout(() => onSearch(next), TYPING_DEBOUNCE_MS);
  };

  /**
   * Both dropdowns search as they are used. Left to a button, a visitor picks Idaho, watches the area codes
   * become Idaho's, and goes on reading a list of numbers from five other states — the results of the
   * opening search, still on screen and looking like an answer to what they just chose.
   */
  const changeState = (value: string) => {
    onLoadAreaCodes(value);
    // A code and a city belong to the state they were chosen in, so neither can outlive a change of state.
    apply({ ...filter, state: value, areaCode: '', city: '' }, 'now');
  };

  const digitsOnly = (value: string, max: number) => value.replace(/\D/g, '').slice(0, max);

  return (
    // The screen fills the main area and keeps its own scrolling inside the list below, so the filters above
    // it stay usable however many numbers came back.
    <Box sx={fillColumnSx}>
      <Heading />

      {/*
        A funnel that already picked a number says so before offering the list again. A lapsed hold is not a
        loss — carrying on reserves the number again — so the wording is about who holds it, not about
        whether it still exists.
      */}
      {chosenNumber != null && (
        <Alert
          severity={chosenHeld ? 'success' : 'info'}
          sx={noticeSx}
          action={
            <Button
              size="small"
              variant="contained"
              disabled={choosing != null}
              onClick={onContinueWithChosen}
            >
              Continue
            </Button>
          }
        >
          {/* The number in bold in both: it is the thing the sentence is about, and the client is comparing
              it with the list below. Trying is what we can promise — the reservation is Telnyx's to give. */}
          {chosenHeld ? (
            <>
              <Box component="span" fontWeight="fontWeightSemiBold">
                {formatSignupNumber(chosenNumber)}
              </Box>
              {' is held for you.'}
            </>
          ) : (
            <>
              {'Your reservation of '}
              <Box component="span" fontWeight="fontWeightSemiBold">
                {formatSignupNumber(chosenNumber)}
              </Box>
              {' has ended. Continue with it and we will try to take it again, or pick another below.'}
            </>
          )}
        </Alert>
      )}

      {/*
        The controls divide the row between them: four inputs at the width one asks for by default come to more
        than the row holds, and the last dropped to a line of its own. Wrapping is kept for narrower widths,
        with the gap on the container — left to itself the button gave, and broke its label across two lines.
      */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        useFlexGap
        flexWrap="wrap"
        spacing={2}
        mb={2}
        alignItems="flex-start"
      >
        {/* All four narrow a list that is already on screen, so none of them is required. */}
        <TextField
          select
          label="State"
          value={filter.state}
          onChange={event => changeState(event.target.value)}
          disabled={searching}
          sx={{ flex: 1, minWidth: 160 }}
          SelectProps={{ displayEmpty: true, inputProps: { 'data-testid': 'signup-state' } }}
          InputLabelProps={{ shrink: true }}
        >
          <MenuItem value="">{ALL_STATES}</MenuItem>
          {US_STATES.map(option => (
            <MenuItem key={option.code} value={option.code}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>

        {/*
          Typed into as well as picked from, and never disabled. As a plain dropdown it could not be used at
          all until a state had been chosen, so somebody who knows their own area code had to first work out
          which state it belongs to. The suggestions are still the state's codes, with how much is behind each.
        */}
        <Autocomplete
          freeSolo
          options={areaCodes}
          loading={loadingAreaCodes}
          inputValue={filter.areaCode}
          getOptionLabel={option => (typeof option === 'string' ? option : option.areaCode)}
          filterOptions={options => options.filter(option => option.areaCode.startsWith(filter.areaCode))}
          onInputChange={(_event, value, reason) => {
            // `reset` fires when a selection rewrites the input, and the selection below has already searched.
            if (reason === 'reset') return;
            apply({ ...filter, areaCode: digitsOnly(value, 3) }, 'afterTyping');
          }}
          onChange={(_event, option) => {
            if (option == null) return;
            const code = typeof option === 'string' ? option : option.areaCode;
            apply({ ...filter, areaCode: code }, 'now');
          }}
          renderOption={(props, option) => {
            const label = countLabel(option.count);
            return (
              <li {...props} key={option.areaCode}>
                {option.areaCode}
                {label != null && (
                  <Typography component="span" variant="body2" color="text.disabled" ml={1}>
                    ({label})
                  </Typography>
                )}
              </li>
            );
          }}
          sx={{ flex: 1, minWidth: 120 }}
          renderInput={params => (
            <TextField
              {...params}
              label="Area code"
              placeholder="Any"
              InputLabelProps={{ shrink: true }}
              inputProps={{
                ...params.inputProps,
                inputMode: 'numeric',
                maxLength: 3,
                'data-testid': 'signup-area-code',
              }}
            />
          )}
        />

        <TextField
          label="City"
          placeholder={companyCity ?? undefined}
          value={filter.city}
          onChange={event => apply({ ...filter, city: event.target.value }, 'afterTyping')}
          sx={{ flex: 1, minWidth: 120 }}
          inputProps={{ maxLength: 60, 'data-testid': 'signup-city' }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Contains"
          placeholder="777"
          value={filter.contains}
          onChange={event => apply({ ...filter, contains: digitsOnly(event.target.value, 7) }, 'afterTyping')}
          sx={{ flex: 1, minWidth: 110 }}
          inputProps={{ maxLength: 7, inputMode: 'numeric', 'data-testid': 'signup-contains' }}
          InputLabelProps={{ shrink: true }}
        />

        {/* Only for a search that failed: the controls above are what runs one in the ordinary case. */}
        {error != null && (
          <Button
            variant="outlined"
            disabled={searching}
            onClick={() => onSearch(filter)}
            sx={{ mt: 1, flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Try again
          </Button>
        )}
      </Stack>

      {error != null && (
        <Alert severity="error" sx={noticeSx}>
          {error}
        </Alert>
      )}

      {/*
        The progress bar and the notices are laid over the list rather than stacked above it. Anything added
        to the flow while a search runs pushes the rows down and back, and the row under the cursor is not
        the row that gets clicked.
      */}
      <Box position="relative" sx={{ flex: 1, minHeight: 200, display: 'flex', flexDirection: 'column' }}>
        {searching && (
          <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }} />
        )}

        <DimmableArea dimmed={searching} sx={{
          pt: 1,
          // The rows are the only part that scrolls. Everything a client needs to change the search — the
          // filters, and the line saying how much of the catalogue is on screen — stays put above them.
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}>
          {!searching && numbers.length === 0 && error == null && (
            <Alert severity="info">{emptyResultText(appliedFilter)}</Alert>
          )}

          {/* Telnyx caps the answer and offers no offset, so more matches than rows is the normal case. */}
          {totalResults != null && totalResults > numbers.length && (
            <Typography variant="body2" color="text.secondary">
              {`${sliceText(appliedFilter, numbers.length, totalResults)} Narrow the search to see different ones.`}
            </Typography>
          )}

          {/*
            The same striping the shared table uses (`GridData`): the tint on every second row, and a
            slightly stronger one under the cursor, so a long list of numbers that differ by two digits stays
            readable across the row.
          */}
          <StripedList>
            {numbers.map(number => (
              <ListItem
                key={number.phoneNumber}
                divider
                secondaryAction={
                  <Button
                    variant="contained"
                    disabled={choosing != null}
                    onClick={() => onChoose(number.phoneNumber)}
                  >
                    {choosing === number.phoneNumber ? 'Holding…' : 'Choose'}
                  </Button>
                }
              >
                {/* Formatted for reading; `onChoose` above is handed the number as Telnyx spells it. */}
                <ListItemText primary={formatSignupNumber(number.phoneNumber)} secondary={placeOf(number)} />
              </ListItem>
            ))}
          </StripedList>
        </DimmableArea>
      </Box>
    </Box>
  );
};
